import { useState } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { InfoIcon } from 'lucide-react';
import { overlay } from 'overlay-kit';
import { toast } from 'sonner';
import { api, getErrorMessage } from '@/shared/api/api';
import {
  convertPagination,
  type ResponseWithOriginPagination,
  type ResponseWithPagination,
} from '@/shared/types/response';
import type { User, UserRole } from '@/shared/types/user';
import { Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Text } from '@/shared/ui/text';
import { USER_MANAGEMENT_COLUMNS } from '../model/columns';
import {
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  FILTER_USER_TYPE_OPTIONS,
  type FilterUserType,
  INFO_USER_ROLE_TERM,
} from '../model/schemas';
import { DataTable } from './data-table';

function DeleteButton({ selectedUser }: { selectedUser: User[] }) {
  const queryClient = useQueryClient();

  const { mutate: deleteUser, isPending: isDeletingUser } = useMutation({
    mutationFn: async (userIds: User['id'][]) => {
      return await api.delete(`admin/users`, {
        json: { user_ids: userIds },
      });
    },
    onSuccess: async () => {
      toast.success('계정이 삭제되었습니다.');
      await queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error) => {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
    },
  });

  const handleDelete = async (selectedUser: User[]) => {
    overlay.open(({ isOpen, close }) => (
      <Dialog
        onOpenChange={close}
        open={isOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>계정 삭제 안내</DialogTitle>
            <DialogDescription>
              {selectedUser.length}개의 계정을 삭제하시겠습니까? 삭제한 계정은 복원이 불가합니다.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant={'secondary'}>취소</Button>
            </DialogClose>
            <Button
              onClick={() => {
                deleteUser(selectedUser.map((user) => user.id as User['id']));
                close();
              }}
              pending={isDeletingUser}
              variant={'primary'}
            >
              삭제
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    ));
  };

  return (
    <Button
      disabled={selectedUser.length === 0}
      onClick={() => handleDelete(selectedUser)}
      variant={'secondary'}
    >
      삭제
    </Button>
  );
}

export function UserManagementTab() {
  const [filters, setFilters] = useState<{
    type: 'all' | UserRole;
  }>({
    type: 'all',
  });
  const [selectedUser, setSelectedUser] = useState<User[]>([]);

  const {
    data: queryUserData,
    isLoading: userLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['users', filters.type],
    queryFn: async ({ pageParam = DEFAULT_PAGE }): Promise<ResponseWithPagination<User>> => {
      const roleQuery = filters.type === 'all' ? '' : `role=${filters.type}&`;
      const response = await api.get<ResponseWithOriginPagination<User>>(
        `admin/users?${roleQuery}page=${pageParam}&size=${DEFAULT_PAGE_SIZE}`
      );
      return {
        data: response.data,
        pagination: await convertPagination(response.pagination),
      };
    },
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = DEFAULT_PAGE + allPages.length;
      const totalPages = Math.ceil((lastPage.pagination?.totalCount || 0) / DEFAULT_PAGE_SIZE);
      return nextPage <= totalPages ? nextPage : undefined;
    },
    initialPageParam: DEFAULT_PAGE,
  });

  const data = queryUserData?.pages.flatMap((page) => page.data || []) || [];
  const isLastPage = !hasNextPage;

  const handleLoadMore = () => {
    if (userLoading || isLastPage || isFetchingNextPage) return;
    fetchNextPage().then();
  };

  return (
    <>
      <div className="w-full flex justify-between shrink-0">
        <Select
          onValueChange={(value) => {
            setFilters((prev) => ({
              ...prev,
              type: value as UserRole | 'all',
            }));
          }}
          value={filters.type}
        >
          <SelectTrigger className="bg-white">
            <SelectValue placeholder="검색" />
          </SelectTrigger>
          <SelectContent>
            {FILTER_USER_TYPE_OPTIONS.map((item: FilterUserType) => (
              <SelectItem
                defaultChecked={item.value === 'all'}
                key={item.value}
                value={item.value}
              >
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <DeleteButton selectedUser={selectedUser} />
      </div>

      <DataTable<User, unknown>
        columns={USER_MANAGEMENT_COLUMNS}
        data={data}
        enablePagination={false}
        isLastPage={isLastPage}
        isLoading={userLoading}
        onLoadMore={handleLoadMore}
        onRowSelectionChange={setSelectedUser}
      />

      <section className="gap-3 shrink-0 relative flex px-3 py-3.5 rounded-md bg-zinc-100">
        <div className="flex gap-1.5 items-center">
          <InfoIcon className="size-4 text-muted-foreground" />
          <Text
            className="text-muted-foreground"
            typography="typo-text-m"
          >
            권한 용어 안내
          </Text>
        </div>
        {Object.values(INFO_USER_ROLE_TERM).map((item: { label: string; description: string }) => (
          <Text
            className="text-muted-foreground"
            key={item.label}
            typography="typo-text-m"
          >
            <span className="font-medium">{item.label}</span> :{' '}
            <span className="font-normal">{item.description}</span>
          </Text>
        ))}
      </section>
    </>
  );
}
